import { zodResolver } from '@hookform/resolvers/zod';
import { personNameSchema } from '@baby-tracker/validation';
import { useQueryClient } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';

import { routePaths } from '../../../app/route-paths.js';
import { authClient } from '../../../lib/auth-client.js';
import { authErrorMessage } from '../../../lib/auth-errors.js';
import { Button } from '../../ui/Button.js';
import { Card } from '../../ui/Card.js';
import { FormError } from '../../ui/FormError.js';
import fieldStyles from '../../ui/fields.module.css';
import { TextField } from '../../ui/TextField.js';

type Panel = 'name' | 'email' | 'password' | 'delete';

const nameSchema = z.object({ name: personNameSchema });
const emailSchema = z.object({ email: z.email('Enter a valid email address') });
const passwordSchema = z
    .object({
        currentPassword: z.string().min(1, 'Enter your current password'),
        newPassword: z.string().min(10, 'Use at least 10 characters'),
        confirm: z.string(),
    })
    .refine((values) => values.newPassword === values.confirm, {
        path: ['confirm'],
        message: 'Passwords do not match',
    });
const deleteSchema = z.object({ password: z.string().min(1, 'Enter your password') });

function NamePanel({ current, onDone }: { current: string; onDone: () => void }): ReactElement {
    const queryClient = useQueryClient();
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<z.infer<typeof nameSchema>>({
        resolver: zodResolver(nameSchema),
        defaultValues: { name: current },
    });

    async function onSubmit(values: z.infer<typeof nameSchema>): Promise<void> {
        setServerError(null);
        const result = await authClient.updateUser({ name: values.name });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not update your name'));
            return;
        }
        await queryClient.invalidateQueries({ queryKey: ['me'] });
        onDone();
    }

    return (
        <form
            className={fieldStyles.stack}
            noValidate
            aria-label="Change name"
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <TextField
                label="Name"
                autoComplete="name"
                error={form.formState.errors.name?.message}
                {...form.register('name')}
            />
            <FormError message={serverError} />
            <div className={fieldStyles.row}>
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Save
                </Button>
                <Button variant="ghost" onClick={onDone}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}

function EmailPanel({
    current,
    onDone,
}: {
    current: string;
    onDone: (message: string) => void;
}): ReactElement {
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<z.infer<typeof emailSchema>>({
        resolver: zodResolver(emailSchema),
        defaultValues: { email: '' },
    });

    async function onSubmit(values: z.infer<typeof emailSchema>): Promise<void> {
        setServerError(null);
        const result = await authClient.changeEmail({
            newEmail: values.email,
            callbackURL: routePaths.settings,
        });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not change your email'));
            return;
        }
        onDone(
            `We sent a confirmation link to ${current}. Approve it there to move your account to ${values.email}.`,
        );
    }

    return (
        <form
            className={fieldStyles.stack}
            noValidate
            aria-label="Change email"
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <TextField
                label="New email"
                type="email"
                autoComplete="email"
                hint={`A confirmation goes to ${current} first.`}
                error={form.formState.errors.email?.message}
                {...form.register('email')}
            />
            <FormError message={serverError} />
            <div className={fieldStyles.row}>
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Send confirmation
                </Button>
                <Button
                    variant="ghost"
                    onClick={() => {
                        onDone('');
                    }}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

function PasswordPanel({ onDone }: { onDone: (message: string) => void }): ReactElement {
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<z.infer<typeof passwordSchema>>({
        resolver: zodResolver(passwordSchema),
        defaultValues: { currentPassword: '', newPassword: '', confirm: '' },
    });

    async function onSubmit(values: z.infer<typeof passwordSchema>): Promise<void> {
        setServerError(null);
        const result = await authClient.changePassword({
            currentPassword: values.currentPassword,
            newPassword: values.newPassword,
            revokeOtherSessions: true,
        });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not change your password'));
            return;
        }
        onDone('Password changed. Other devices have been signed out.');
    }

    return (
        <form
            className={fieldStyles.stack}
            noValidate
            aria-label="Change password"
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <TextField
                label="Current password"
                type="password"
                autoComplete="current-password"
                error={form.formState.errors.currentPassword?.message}
                {...form.register('currentPassword')}
            />
            <TextField
                label="New password"
                type="password"
                autoComplete="new-password"
                error={form.formState.errors.newPassword?.message}
                {...form.register('newPassword')}
            />
            <TextField
                label="Confirm new password"
                type="password"
                autoComplete="new-password"
                error={form.formState.errors.confirm?.message}
                {...form.register('confirm')}
            />
            <FormError message={serverError} />
            <div className={fieldStyles.row}>
                <Button type="submit" loading={form.formState.isSubmitting}>
                    Change password
                </Button>
                <Button
                    variant="ghost"
                    onClick={() => {
                        onDone('');
                    }}
                >
                    Cancel
                </Button>
            </div>
        </form>
    );
}

function DeletePanel({ onCancel }: { onCancel: () => void }): ReactElement {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [serverError, setServerError] = useState<string | null>(null);
    const form = useForm<z.infer<typeof deleteSchema>>({
        resolver: zodResolver(deleteSchema),
        defaultValues: { password: '' },
    });

    async function onSubmit(values: z.infer<typeof deleteSchema>): Promise<void> {
        setServerError(null);
        const result = await authClient.deleteUser({ password: values.password });
        if (result.error) {
            setServerError(authErrorMessage(result.error, 'Could not delete your account'));
            return;
        }
        queryClient.clear();
        await navigate(routePaths.signIn);
    }

    return (
        <form
            className={fieldStyles.stack}
            noValidate
            aria-label="Delete account"
            onSubmit={(event) => {
                void form.handleSubmit(onSubmit)(event);
            }}
        >
            <p>
                Deleting your account removes your sign-in, your memberships and every family where
                you are the only member, including its photos. Records you logged in shared families
                stay with that family. If you are the only owner of a family with other members,
                make someone else an owner first.
            </p>
            <TextField
                label="Confirm with your password"
                type="password"
                autoComplete="current-password"
                error={form.formState.errors.password?.message}
                {...form.register('password')}
            />
            <FormError message={serverError} />
            <div className={fieldStyles.row}>
                <Button type="submit" variant="danger" loading={form.formState.isSubmitting}>
                    Delete my account
                </Button>
                <Button variant="ghost" onClick={onCancel}>
                    Cancel
                </Button>
            </div>
        </form>
    );
}

export function AccountSection(): ReactElement {
    const session = authClient.useSession();
    const navigate = useNavigate();
    const [panel, setPanel] = useState<Panel | null>(null);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [pending, setPending] = useState<'out' | 'others' | null>(null);
    const user = session.data?.user;

    function finish(nextMessage: string): void {
        setPanel(null);
        setMessage(nextMessage.length > 0 ? nextMessage : null);
    }

    async function signOut(): Promise<void> {
        setPending('out');
        setError(null);
        const result = await authClient.signOut();
        setPending(null);
        if (result.error) {
            setError(authErrorMessage(result.error, 'Could not sign out'));
            return;
        }
        await navigate(routePaths.signIn);
    }

    async function signOutOthers(): Promise<void> {
        setPending('others');
        setError(null);
        setMessage(null);
        const result = await authClient.revokeOtherSessions();
        setPending(null);
        if (result.error) {
            setError(authErrorMessage(result.error, 'Could not sign out other devices'));
            return;
        }
        setMessage('Other devices have been signed out.');
    }

    return (
        <Card title="Account">
            <p>
                {user?.name}
                {user ? ` · ${user.email}` : ''}
            </p>
            <FormError message={error} />
            {message ? <p role="status">{message}</p> : null}
            {panel === 'name' && user ? (
                <NamePanel
                    current={user.name}
                    onDone={() => {
                        finish('Name updated.');
                    }}
                />
            ) : null}
            {panel === 'email' && user ? <EmailPanel current={user.email} onDone={finish} /> : null}
            {panel === 'password' ? <PasswordPanel onDone={finish} /> : null}
            {panel === 'delete' ? (
                <DeletePanel
                    onCancel={() => {
                        finish('');
                    }}
                />
            ) : null}
            {panel === null ? (
                <div className={fieldStyles.row}>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setMessage(null);
                            setPanel('name');
                        }}
                    >
                        Change name
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setMessage(null);
                            setPanel('email');
                        }}
                    >
                        Change email
                    </Button>
                    <Button
                        variant="secondary"
                        onClick={() => {
                            setMessage(null);
                            setPanel('password');
                        }}
                    >
                        Change password
                    </Button>
                </div>
            ) : null}
            {panel === null ? (
                <div className={fieldStyles.row}>
                    <Button
                        variant="secondary"
                        loading={pending === 'others'}
                        onClick={() => {
                            void signOutOthers();
                        }}
                    >
                        Sign out other devices
                    </Button>
                    <Button
                        variant="danger"
                        loading={pending === 'out'}
                        onClick={() => {
                            void signOut();
                        }}
                    >
                        Sign out
                    </Button>
                    <Button
                        variant="ghost"
                        onClick={() => {
                            setMessage(null);
                            setPanel('delete');
                        }}
                    >
                        Delete account
                    </Button>
                </div>
            ) : null}
        </Card>
    );
}
